import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCompanyRiskData } from '@/hooks/useCompanyRiskData';
import { useCompanyReviews } from '@/hooks/useCompanyReviews';
import { getRiskConfig } from '@/utils/riskConfigurations';
import RiskColorChart from '@/components/company-profile/RiskColorChart';
import BackButton from '@/components/ui/back-button';

const CompanyComparisonResults = () => {
  const [searchParams] = useSearchParams();
  const company1Name = searchParams.get('company1') || '';
  const company2Name = searchParams.get('company2') || '';

  // Get review data for both companies
  const { allCompanyReviews: company1Reviews } = useCompanyReviews(company1Name);
  const { allCompanyReviews: company2Reviews } = useCompanyReviews(company2Name);

  // Get risk data for both companies
  const company1RiskData = useCompanyRiskData({
    companyName: company1Name,
    reviewCount: company1Reviews.length,
    reviews: company1Reviews
  });

  const company2RiskData = useCompanyRiskData({
    companyName: company2Name,
    reviewCount: company2Reviews.length,
    reviews: company2Reviews
  });

  // Get risk configurations for styling
  const company1RiskConfig = getRiskConfig(company1Name, company1RiskData.riskLevel);
  const company2RiskConfig = getRiskConfig(company2Name, company2RiskData.riskLevel);

  const CompanyRiskCard = ({ 
    companyName, 
    riskLevel, 
    riskConfig, 
    reviewCount 
  }: { 
    companyName: string; 
    riskLevel: any; 
    riskConfig: any; 
    reviewCount: number; 
  }) => {
    const { icon: RiskIcon, color, textColor, borderColor, iconColor, description } = riskConfig;

    return (
      <Card className={`border ${borderColor}`}>
        <CardHeader>
          <CardTitle className="text-center text-lg">{companyName}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`${color} rounded-lg p-6 mb-4`}>
            <div className="flex flex-col items-center gap-4">
              <div className="flex-shrink-0">
                <div className={`p-3 rounded-full ${color} border ${borderColor}`}>
                  <RiskIcon className={`w-8 h-8 ${iconColor}`} />
                </div>
              </div>
              
              <div className="text-center">
                <h3 className={`text-xl font-semibold ${textColor} mb-2`}>
                  {riskLevel}
                </h3>
                <p className="text-gray-700 text-sm">{description}</p>
              </div>
            </div>
          </div>
          
          <RiskColorChart riskLevel={riskLevel} />
          
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Based on {reviewCount} reviews and AI analysis
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          <BackButton className="mb-6" />
          
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-redflags-dark mb-4">
              Company Comparison Results
            </h1>
            <p className="text-redflags-gray">
              Redundancy risk assessment comparison
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <CompanyRiskCard
              companyName={company1Name}
              riskLevel={company1RiskData.riskLevel}
              riskConfig={company1RiskConfig}
              reviewCount={company1Reviews.length}
            />

            <CompanyRiskCard
              companyName={company2Name}
              riskLevel={company2RiskData.riskLevel}
              riskConfig={company2RiskConfig}
              reviewCount={company2Reviews.length}
            />
          </div>

          {/* Summary comparison */}
          <Card className="mt-8 max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="text-center">Comparison Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center">
                  <h4 className="font-semibold text-lg mb-2">{company1Name}</h4>
                  <p className="text-sm text-gray-600">
                    Risk Level: <span className={company1RiskConfig.textColor}>{company1RiskData.riskLevel}</span>
                  </p>
                  <p className="text-sm text-gray-600">Reviews: {company1Reviews.length}</p>
                </div>
                
                <div className="text-center">
                  <h4 className="font-semibold text-lg mb-2">{company2Name}</h4>
                  <p className="text-sm text-gray-600">
                    Risk Level: <span className={company2RiskConfig.textColor}>{company2RiskData.riskLevel}</span>
                  </p>
                  <p className="text-sm text-gray-600">Reviews: {company2Reviews.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyComparisonResults;
